import React, { useState } from "react";
import { Plus, ChevronRight } from "lucide-react";
import loader from "../../../../../components/recharts/loader.gif";
import { AddBatteryModal, BatteryRow } from "../../PowerProcurement/AddBattery";

interface BatteryOffer {
  start_date: string;
  end_date: string;
  id: number;
  battery_id: number;
  battery_capacity_in_mwh: number;
  chargingRate: string;
  vc: string;
  fc: string;
  status?: boolean;
  offerType: "battery";
}

interface BatteryConsoleProps {
  batteries: BatteryOffer[];
  loading: boolean;
  error: string | null;
  isSliderOpen: boolean;
  onAddBattery: (data: BatteryRow[]) => Promise<void>;
  onToggleOffer: (id: number) => void;
  isReadOnly?: boolean;
}

export function BatteryConsole({
  batteries,
  loading,
  error,
  isSliderOpen,
  onAddBattery,
  onToggleOffer,
  isReadOnly = false,
}: BatteryConsoleProps) {

  const [isAddBatteryModalOpen, setIsAddBatteryModalOpen] = useState(false);
  const [batteryScrollIndex, setBatteryScrollIndex] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);

  const handleAddBattery = () => {
    setIsAddBatteryModalOpen(true);
  };

  const handleAddBatterySubmit = async (data: BatteryRow[]) => {
    setModalLoading(true);
    try {
      await onAddBattery(data);
      setIsAddBatteryModalOpen(false);
    } catch (err) {
      console.error("Error adding battery:", err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleBatteryScroll = (direction: "up" | "down") => {
    const activeBatteries = batteries.filter((b) => b.status);
    if (direction === "down" && batteryScrollIndex < activeBatteries.length - 1) {
      setBatteryScrollIndex(batteryScrollIndex + 1);
    } else if (direction === "up" && batteryScrollIndex > 0) {
      setBatteryScrollIndex(batteryScrollIndex - 1);
    }
  };

  // Active offers for condensed view
  const activeBatteries = batteries.filter((b) => b.status);
  const visibleBatteries = activeBatteries.slice(batteryScrollIndex, batteryScrollIndex + 1);

  // Condensed View (when slider is closed)


  // Full View (when slider is open)
  return (
    <>
      <AddBatteryModal
        isModalOpen={isAddBatteryModalOpen}
        setIsModalOpen={setIsAddBatteryModalOpen}
        modalTitle="Add Battery"
        onSubmit={handleAddBatterySubmit}
        loading={modalLoading}
        setLoading={setModalLoading}
      />

      <div className="bg-white rounded-xl border border-[#5B8A9F] p-1.5 mb-2">
        <div className="flex justify-between items-center pb-2 border-b border-[#E8F4F8] mb-2">
          <h1 className="text-sm font-medium text-[#9AA0A6]">
            BESS Capacity
          </h1>
          <button
            onClick={handleAddBattery}
            disabled={isReadOnly}
            className={`w-6 h-6 flex items-center justify-center rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isReadOnly
              ? "bg-gray-100 text-gray-400 border-gray-200"
              : "border-[#5B8A9F] text-[#5B8A9F] hover:bg-[#5B8A9F] hover:text-white"
              }`}
          >
            <Plus strokeWidth={2.5} className="w-4 h-4" />
          </button>
        </div>

        <div className="!overflow-auto !max-h-[15.2vh] custom-scroll pr-1">
          {loading && batteries.length === 0 ? (
            <div className="h-full w-full bg-white flex items-center justify-center ">
              <img
                src={loader}
                width={200}
                alt="loader"
                className="block mt-[2rem]"
              />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : batteries.length === 0 ? (
            <div className="text-gray-600 text-center py-8 border border-dashed border-[#5B8A9F] rounded-lg bg-[#F5F9FB]">
              <h2 className="mb-1 text-sm text-gray-600">
                No batteries added yet
              </h2>
              <p className="text-xs text-gray-400">
                Click "+" to get started
              </p>
            </div>
          ) : (
            batteries.map((battery, index) => (
              <div
                key={`battery-${battery.id}`}
                className="border border-[#E8F4F8] rounded-lg p-1.5 bg-white mb-1.5 relative mt-2"
              >
                <div className="absolute top-[-13px] left-2 bg-white px-1">
                  <span className="text-[10px] font-bold text-[#5B8A9F]">
                    BESS {index + 1}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-y-1 gap-x-1 mt-0.5">
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      Start Date
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {battery.start_date || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      End Date
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {battery.end_date || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      Quantum
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {battery.battery_capacity_in_mwh
                        ? `${battery.battery_capacity_in_mwh} MW`
                        : "N/A"}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      Charge Rate
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {battery.chargingRate
                        ? `${battery.chargingRate} MW`
                        : "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      Fixed Charge
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {battery.fc ? `${battery.fc}` : "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      VC
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {battery.vc ? `${battery.vc}` : "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}